import { Component, OnInit } from '@angular/core';
import { ITableConfig } from 'projects/common/src/app/interfaces/table/table-config';
import { ITableDataParams } from 'projects/common/src/app/interfaces/table/table-data-params';
import { ITableHeaderParams } from 'projects/common/src/app/interfaces/table/table-header-params';
import { ITableLargeColumnConfig } from 'projects/common/src/app/interfaces/table/table-large-column-config';
import { CacheService } from 'projects/common/src/app/services';
import { LanguageService } from 'projects/common/src/app/services/parameters';
import { TableConfigService } from 'projects/common/src/app/services/table/table-config.service';
import { ExcelExportService } from 'projects/common/src/app/services/utils/excel-export.service';
import { IFilterConfig } from 'src/app/modules/core/interfaces/filter-config';
import { IOutputFilter } from 'src/app/modules/core/interfaces/output-filter';
import { DatePresentationPipe } from 'src/app/modules/core/pipes/date-presentation.pipe';
import { GoogleAnalyticsService } from 'src/app/modules/core/services/google/google-analytics.service';
import { ILayoutConfig } from 'src/app/modules/layout/interfaces/layout-config';
import { LayoutConfigService } from 'src/app/modules/layout/services/layout-config.service';
import { ICartirProduction } from '../../interfaces/report-cartir-production';
import { ReportCartirProductionService } from '../../services/report-cartir-production.service';
import { PermissionService } from 'src/app/modules/core/services/parameter/permission/permission.service';
import { EPermissions } from 'projects/common/src/app/enumerables/permissions';

@Component({
  selector: 'app-report-cartir',
  templateUrl: './report-cartir.component.html',
  styleUrls: ['./report-cartir.component.scss'],
})
export class ReportCartirComponent implements OnInit {
  public tableConfig: ITableConfig;
  public selectedFilters: IOutputFilter;
  public filterConfig: IFilterConfig;
  public dataReport: any;
  public params: IOutputFilter;
  public message: string;
  public hasData: boolean;
  public systemError: string;
  public btnSpinner: boolean;
  public disabledExcelBtn: boolean;
  public readonly PERMISSION_OBJECT = 'LEY DE MINERAL';
  public canVisualize = false;

  constructor(
    private reportCartirProductionService: ReportCartirProductionService,
    private layoutConfigService: LayoutConfigService,
    private tableConfigService: TableConfigService,
    private languageService: LanguageService,
    private datePipe: DatePresentationPipe,
    private cacheService: CacheService,
    private googleAnalyticsService: GoogleAnalyticsService,
    private _excelExportService: ExcelExportService,
    private permissionService: PermissionService,
  ) {
    this.hasData = false;
    this.message = '';

    this.filterConfig = {
      dates: { required: true, options: { minMonth: 36 } },
      shift: { required: true },
      allMine: { required: true },
      cleanButton: true,
    };
  }

  ngOnInit(): void {
    const permissions = this.permissionService.getPermissionsByObj(this.PERMISSION_OBJECT);
    this.canVisualize = permissions.some(({ permission }) => permission.toLowerCase() === EPermissions.VISUALIZE.toLowerCase());
    this.setHeader();
  }
  private setHeader(): void {
    const config: ILayoutConfig = {
      title: 'reports',
      subtitle: 'cartir-production',
      imageKey: 'report',
      imageType: 'section',
    };

    this.layoutConfigService.setConfig(config);
  }

  //* * Fill table cartir-production */
  public async getDataReportCartirProduction($event): Promise<void> {
    this.filterConfig.isLoading = true;
    this.params = {
      startDate: $event.startDate,
      endDate: $event.endDate,
      shiftId: $event.shiftId,
      mineId: $event.zoneId,
      companyId: this.cacheService.local('session').user.company.id,
    };

    this.reportCartirProductionService.GetReportCartir(this.params).then((result: ICartirProduction[]) => {
      const tableData = [];

      for (const itemCartirProduction of result) {
        tableData.push({
          shiftName: itemCartirProduction.shiftName,
          sector: itemCartirProduction.sector,
          streetName: itemCartirProduction.streetName,
          extractionPoint: itemCartirProduction.extractionPoint,
          pailQuantity: itemCartirProduction.pailQuantity,
          additionalPailQuantity: itemCartirProduction.additionalPailQuantity,
          taskStart: itemCartirProduction.taskStart,
          lastStatus: itemCartirProduction.lastStatus,
          lastStatusAt: itemCartirProduction.lastStatusAt,
          lastCondition: itemCartirProduction.lastCondition,
          lastConditionAt: itemCartirProduction.lastConditionAt,
          reporterName: itemCartirProduction.reporterName,
          grade: itemCartirProduction.grade != null ? `${itemCartirProduction.grade.toFixed(2)}%` : `${0}%`,
        });
      }
      this.filterConfig.isLoading = false;
      if (result?.length) {
        this.setTableDetailsConfig(tableData);
        this.hasData = true;
        this.message = '';
      } else {
        this.hasData = false;
        this.message = this.languageService.message('m034');
        this.filterConfig.isLoading = false;
      }
    });
    const filters = {
      reports: 'report-cartir',
    };
    this.googleAnalyticsService.sendCustomEvent('filter', filters);
  }

  //* * Set table config report cartir-production */
  public setTableDetailsConfig(tableData: any): void {
    const customLargeColumns: ITableLargeColumnConfig[] = [
      { field: 'shiftName', isLargeColumn: true, flexGrow: 0.8, customPadding: '6px 0px 0px 8px' },
      { field: 'sector', isLargeColumn: true, flexGrow: 0.8, customPadding: '6px 0px 0px 3px' },
      { field: 'streetName', isLargeColumn: true, flexGrow: 0.8, customPadding: '6px 0px 0px 3px' },
      { field: 'extractionPoint', isLargeColumn: true, flexGrow: 0.8, customPadding: '6px 0px 0px 3px' },
      { field: 'pailQuantity', isLargeColumn: true, flexGrow: 1, customPadding: '6px 0px 0px 3px' },
      { field: 'additionalPailQuantity', isLargeColumn: true, flexGrow: 1, customPadding: '6px 0px 0px 3px' },
      { field: 'reporterName', isLargeColumn: true, flexGrow: 1.5, customPadding: '6px 0px 0px 3px' },
      { field: 'taskStart', isLargeColumn: true, flexGrow: 1, customPadding: '6px 0px 0px 3px' },
    ];
    const excludeColumns = !this.canVisualize
      ? ['machineId', 'machineTypeId', 'zoneId', 'shiftId', 'grade']
      : ['machineId', 'machineTypeId', 'zoneId', 'shiftId'];

    const headerParams: ITableHeaderParams = {
      data: tableData,
      excludeColumns,
      replaceColumnName: [
        { prevName: 'zoneName', newName: 'zone' },
        { prevName: 'pailQuantity', newName: 'shelves' },
        { prevName: 'streetName', newName: 'street' },
        { prevName: 'extractionPoint', newName: 'ditch' },
        { prevName: 'additionalPailQuantity', newName: 'remnant-shelves' },
        { prevName: 'taskStart', newName: 'cartir-start' },
        { prevName: 'reporterName', newName: 'reported by' },
        { prevName: 'lastStatusAt', newName: 'date' },
        { prevName: 'lastCondition', newName: 'last-condition' },
        { prevName: 'lastConditionAt', newName: 'date' },
        { prevName: 'grade', newName: 'grade-default-name' },
      ],
      sortables: true,
      customLargeColumns,
    };
    const datasetParams: ITableDataParams = {
      tableData,
      excludeColumns,
      dateConfig: [
        { field: 'taskStart', format: 'date-time' },
        { field: 'lastStatusAt', format: 'date-time' },
        { field: 'lastConditionAt', format: 'date-time' },
      ],
      customLargeColumns,
    };

    const header = this.tableConfigService.setHeaderConfig(headerParams);
    const dataset = this.tableConfigService.setDatasetConfig(datasetParams);

    this.tableConfig = { header, dataset };
  }

  /**
   * Executes excel download
   */
  public downloadExcel(): void {
    const selectRangeDate = {
      startDate: this.datePipe.transform(this.params.startDate),
      endDate: this.datePipe.transform(this.params.endDate),
    };
    this.btnSpinner = true;
    this.disabledExcelBtn = true;
    this.reportCartirProductionService.GetReportCartirExcel(this.params).subscribe({
      next: (res) => {
        this._excelExportService.exportAsExcel(res, 'rpt_cartir_and_production', selectRangeDate, null, false);
        this.btnSpinner = false;
        this.disabledExcelBtn = false;
        this._excelExportService.messageToastSuccess();
      },
      error: (error) => {
        console.error(error);
        this.btnSpinner = false;
        this.disabledExcelBtn = false;
        this._excelExportService.messageToastError();
      },
    });
  }
}
